<template>
  <div class="ai-chat-composer">
    <div
      class="_box"
      @click="focus"
    >
      <div class="_input">
        <m-editor
          ref="editor"
          :allowed-content="allowedContent"
          :placeholder="$t('aiChat.inputPlaceholder')"
          :enter-handlers="[onEnter]"
          split-key="Shift-Enter"
          disable-mobile-menu
          @input="onInput"
        />
      </div>
      <div class="_actions">
        <m-tooltip placement="top">
          <m-btn
            class="_send"
            icon="send"
            color="primary"
            fab
            small
            :disabled="!canSend"
            @click="submit"
          />
          <template #title>
            {{ $t('aiChat.send') }}
          </template>
        </m-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup>
import MEditor from '@/components/editor/MEditor.vue';
import { computed, ref } from 'vue';
import { docToMarkdown } from '@/lib/markdown';
import { editorNodeType } from 'shared/constants.json';

const props = defineProps({
  pending: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['send']);

const allowedContent = [editorNodeType.bulletList, editorNodeType.orderedList];

const editor = ref(null);
const draft = ref(null);

const markdown = computed(() => docToMarkdown(draft.value));
const canSend = computed(() => !props.pending && markdown.value !== '');

const focus = () => editor.value.focus();

const onInput = (doc) => { draft.value = doc; };

const submit = () => {
  if (!canSend.value) {
    return;
  }
  emit('send', markdown.value);
  editor.value.clearContent();
  draft.value = null;
};

// the emoji and slash menus are plugins, and plugin handlers run before every keymap, so an
// open menu takes Enter before it can reach this
const onEnter = () => {
  submit();
  return true;
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  @import "shared/assets/scss/_box-shadow.scss";

  .ai-chat-composer {
    position: relative;
    display: flex;
    flex-shrink: 0;
    max-height: 30%;
    padding: 0 1.2rem 1.2rem;

    // the conversation ends at the top edge of this box, so it would otherwise cut off mid
    // message; the fade sits above the box and stops where the box's right edge does, so it
    // never paints over the list's scroll bar
    &::before {
      content: '';
      position: absolute;
      right: 1.2rem;
      bottom: 100%;
      left: 0;
      height: $ai-chat-composer-fade-height;
      background: linear-gradient(to top, white, rgba(255, 255, 255, 0));
      pointer-events: none;
    }

    ._box {
      // positioned so it paints after the fade above it, which would otherwise cover the top
      // of the 1px ring that draws the box's edge
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      min-height: 0;
      padding: .8rem;
      background-color: white;
      border-radius: 2rem;
      cursor: text;

      @include box_shadow(1);
    }

    ._input {
      flex: 1;
      min-height: 0;
      padding: .4rem .2rem;
      overflow-y: auto;
    }

    ._actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-shrink: 0;
      gap: .4rem;
      padding: .2rem;
    }

    ._send {
      border-radius: 50%;
    }
  }
</style>
